import { createTheme } from '@mui/material';
import BentonSansRegular from '../assets/fonts/BentonSans-Regular.otf';
import BentonSansMedium from '../assets/fonts/BentonSans-Medium.otf';
import BentonSansBold from '../assets/fonts/BentonSans-Bold.otf';
import AvantGardeMedium from '../assets/fonts/Avant-Garde-Gothic-LT-Medium.ttf';
import AvantGardeMediumItalic from '../assets/fonts/Avant-Garde-Gothic-LT-Medium-Oblique.ttf';
import AvantGardeBold from '../assets/fonts/Avant-Garde-Gothic-LT-Bold.ttf';
import AvantGardeBoldItalic from '../assets/fonts/Avant-Garde-Gothic-LT-Bold-Oblique.ttf';

export const phioBrandColors = {
    PRIMARY: '#424c5f',
    SECONDARY: '#26303b',
    ACTIVE: '#0981fd',
    INPUTBORDER: '#94979a',
    DISABLED: '#74777a',
    HOVER: 'rgba(67, 134, 249, 0.6)',
    DIVIDER: 'rgba(255, 255, 255, 0.12)',
};

export const phioBreakpoints = {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
};

export const fontFamily = 'BentonSans,"Roboto","Helvetica","Arial",sans-serif';
export const headerFontFamily =
    'AvantGarde,"Roboto","Helvetica","Arial",sans-serif';

export const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
});

export default createTheme({
    breakpoints: {
        ...theme.breakpoints,
    },
    typography: {
        fontFamily,
        h1: {
            fontFamily: headerFontFamily,
        },
        h2: {
            fontFamily: headerFontFamily,
        },
        h3: {
            fontFamily: headerFontFamily,
        },
        h4: {
            fontFamily: headerFontFamily,
        },
        h5: {
            fontFamily: headerFontFamily,
        },
        h6: {
            fontFamily: headerFontFamily,
        },
    },
    palette: {
        mode: 'dark',
        primary: {
            main: phioBrandColors.ACTIVE,
        },
        secondary: {
            main: '#ffffff',
        },
    },
    components: {
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: `
                @font-face {
                    font-family: BentonSans;
                    font-style: normal;
                    font-display: swap;
                    font-weight: 700;
                    src: url(${BentonSansBold}) format("opentype");
                }
                @font-face {
                    font-family: BentonSans;
                    font-style: normal;
                    font-display: swap;
                    font-weight: 500;
                    src: url(${BentonSansMedium}) format("opentype");
                }
                @font-face {
                    font-family: BentonSans;
                    font-style: normal;
                    font-display: swap;
                    font-weight: 400;
                    src: url(${BentonSansRegular}) format("opentype");
                }
                @font-face {
                    font-family: AvantGarde;
                    font-style: normal;
                    font-display: swap;
                    font-weight: 500;
                    src: url(${AvantGardeMedium}) format('truetype');
                }
                @font-face {
                    font-family: AvantGarde;
                    font-style: italic;
                    font-display: swap;
                    font-weight: 500;
                    src: url(${AvantGardeMediumItalic}) format('truetype');
                }
                @font-face {
                    font-family: AvantGarde;
                    font-style: normal;
                    font-display: swap;
                    font-weight: 700;
                    src: url(${AvantGardeBold}) format('truetype');
                }
                @font-face {
                    font-family: AvantGarde;
                    font-style: italic;
                    font-display: swap;
                    font-weight: 700;
                    src: url(${AvantGardeBoldItalic}) format('truetype');
                }
                html,
                body {
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(to bottom, #2d4155 0%, #0d1925 100%);
                    background-repeat: no-repeat;
                    background-attachment: fixed;
                    min-height: 100%;
                    margin: 0;
                    overflow: -moz-scrollbars-none;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                    letter-spacing: normal; 
                }
                #root {
                    width: 100%;
                    height: 100%;
                    position: relative;
                }
                ul {
                    padding: 0;
                    margin: 0;
                }
            `,
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: '12px',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    lineHeight: '1.7em',
                    color: '#ffffff',
                },
                outlined: {
                    color: phioBrandColors.INPUTBORDER,
                    '&.Mui-focused': {
                        color: '#ffffff',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '&:hover $notchedOutline': {
                        borderColor: '#ffffff',
                    },
                    '&$focused $notchedOutline': {
                        borderColor: '#ffffff',
                    },
                },
                input: {
                    caretColor: '#ffffff',
                    color: '#ffffff',
                    '&::placeholder': {
                        color: '#ffffff',
                    },
                },
                notchedOutline: {
                    borderColor: phioBrandColors.INPUTBORDER,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: phioBrandColors.PRIMARY,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '28px',
                    textTransform: 'none',
                    paddingTop: '9px',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    '&.Mui-selected': {
                        backgroundColor: phioBrandColors.ACTIVE,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: phioBrandColors.ACTIVE,
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(67, 134, 249, 0.6)',
                    },
                },
            },
        },
    },
});
